import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  FaWhatsapp, FaArrowRight, FaPhoneAlt,
  FaTrophy, FaLock, FaBolt, FaCheckDouble,
} from 'react-icons/fa';
import SolutionsCarousel from '../components/SolutionsCarousel/SolutionsCarousel';
import BlurText from '../components/BlurText/BlurText';
import './HeroSection.css';

/* ─── Static data ─────────────────────────────── */
const STATS = [
  { value: '20+',  label: 'Years Experience' },
  { value: '3000+', label: 'Happy Clients'     },
  { value: '10+',  label: 'IT Solutions'      },
];

const TRUST = [
  { icon: <FaTrophy />, label: 'Excellence Since 2006' },
  { icon: <FaLock   />, label: 'ISO Certified Security' },
  { icon: <FaBolt   />, label: '24/7 Priority Support'  },
];

const HERO_PHRASES = [
  'Business Intelligence',
  'Smart Business Accounting',
  'IT Cloud Infrastructure',
  'Custom App Development',
  'Biometric Access Systems'
];

const fadeUp = {
  hidden:  { opacity: 0, y: 30 },
  visible: (i) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.8, delay: i * 0.1, ease: [0.25, 0.1, 0.25, 1] },
  }),
};

/* ─── HeroSection ─────────────────────────────── */
const HeroSection = ({ splashState }) => {
  const navigate = useNavigate();
  const isSplashDone = splashState === 'completed';
  const [firstLineDone, setFirstLineDone] = useState(false);
  const [markerActive, setMarkerActive] = useState(false);
  const [phraseIdx, setPhraseIdx] = useState(0);
  const [textAnimState, setTextAnimState] = useState('entered'); // 'entered' | 'exiting' | 'entering'

  // Easter Egg: Triple-click on the first stat (20+) reveals founder note
  const [showFounderNote, setShowFounderNote] = useState(false);
  const clickTimerRef = useCallback(() => {}, []);
  const clickCountRef = React.useRef(0);
  const clickTimerIdRef = React.useRef(null);

  const handleStatTripleClick = () => {
    clickCountRef.current += 1;
    clearTimeout(clickTimerIdRef.current);
    clickTimerIdRef.current = setTimeout(() => {
      if (clickCountRef.current >= 3) setShowFounderNote(true);
      clickCountRef.current = 0;
    }, 400);
  };

  // Reset completion state if splash state changes (for debugging/refreshes)
  useEffect(() => {
    if (!isSplashDone) {
      setFirstLineDone(false);
      setMarkerActive(false);
      setPhraseIdx(0);
      setTextAnimState('entered');
    }
  }, [isSplashDone]);

  // Initial trigger of marker stroke once first line reveals
  useEffect(() => {
    if (firstLineDone) {
      const t = setTimeout(() => {
        setMarkerActive(true);
      }, 350);
      return () => clearTimeout(t);
    } else {
      setMarkerActive(false);
    }
  }, [firstLineDone]);

  // Smooth rotation to next phrase
  const rotatePhrase = useCallback(() => {
    setMarkerActive(false);
    setTextAnimState('exiting'); // slide down & fade out

    setTimeout(() => {
      setPhraseIdx((prev) => (prev + 1) % HERO_PHRASES.length);
      setTextAnimState('entering'); // reset to top instantly

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setTextAnimState('entered'); // slide in & fade in
          setTimeout(() => {
            setMarkerActive(true); // draw green underline
          }, 320);
        });
      });
    }, 320);
  }, []);

  // Automatically cycle through phrases every 3.8s
  useEffect(() => {
    if (!firstLineDone) return;

    const interval = setInterval(() => {
      rotatePhrase();
    }, 3800);

    return () => clearInterval(interval);
  }, [firstLineDone, rotatePhrase]);

  return (
    <div className="hero-container" id="home">
      <div className="hero-bg"     aria-hidden="true" />
      <div className="hero-grid"   aria-hidden="true" />
      <div className="hero-swoosh" aria-hidden="true" />

      <section className="hero-main">
        <div className="hero-grid-layout">

          {/* ── Left Content ── */}
          <div className="hero-content-left">
            <motion.div
              className="hero-badge"
              variants={fadeUp} initial="hidden" animate="visible" custom={0}
            >
              <FaCheckDouble className="hero-badge__tick" />
              Trusted Tally 5-Star Partner Since 2006
            </motion.div>

            <h1 className="hero-title hero-title--blur">
              {isSplashDone ? (
                <BlurText
                  text="Leading the Future of"
                  delay={120}
                  animateBy="words"
                  direction="top"
                  stepDuration={0.4}
                  threshold={0.05}
                  className="hero-blur-line"
                  onAnimationComplete={() => setFirstLineDone(true)}
                />
              ) : (
                /* Invisible placeholder to prevent layout shifts */
                <span className="hero-blur-line" style={{ opacity: 0 }}>Leading the Future of</span>
              )}
              
              <span 
                className={`highlight-text hero-second-line hero-highlight-wrapper${firstLineDone ? ' hero-second-line--visible' : ''}`}
                style={{ cursor: 'pointer', display: 'inline-block', position: 'relative', verticalAlign: 'bottom' }}
                onClick={rotatePhrase}
                title="Click to alternate core focus area"
              >
                <span 
                  id="hero-interactive-text" 
                  style={{ 
                    display: 'inline-block',
                    position: 'relative',
                    zIndex: 2,
                    transition: textAnimState === 'entering' 
                      ? 'none' 
                      : 'transform 0.36s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.32s ease',
                    transform: textAnimState === 'exiting' 
                      ? 'translateY(14px)' 
                      : textAnimState === 'entering' 
                        ? 'translateY(-14px)' 
                        : 'translateY(0)',
                    opacity: textAnimState === 'entered' ? 1 : 0
                  }}
                >
                  {HERO_PHRASES[phraseIdx]}
                </span>

                {/* ── Real Handmade Underline SVG (Matches Text Green) ── */}
                <svg
                  className={`handmade-underline-svg ${markerActive ? 'handmade-underline-svg--drawn' : ''}`}
                  viewBox="0 0 500 24"
                  fill="none"
                  preserveAspectRatio="none"
                  aria-hidden="true"
                >
                  <defs>
                    {/* Exact Text Sage Green Gradient */}
                    <linearGradient id="handmadeUnderlineGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%"   stopColor="#4d5a37" />
                      <stop offset="35%"  stopColor="#6B7A52" />
                      <stop offset="70%"  stopColor="#8a9a58" />
                      <stop offset="100%" stopColor="#55643e" />
                    </linearGradient>

                    {/* Subtle Organic Hand Tremor & Ink Texture */}
                    <filter id="handDrawnFilter" x="-5%" y="-50%" width="110%" height="200%">
                      <feTurbulence type="fractalNoise" baseFrequency="0.04" numOctaves="2" result="paperNoise" />
                      <feDisplacementMap in="SourceGraphic" in2="paperNoise" scale="1.8" xChannelSelector="R" yChannelSelector="G" />
                    </filter>
                  </defs>

                  {/* Single sweeping realistic handmade underline */}
                  <path
                    d="M 6 13 C 85 17.5, 180 14, 275 15.5 C 365 17, 440 13.5, 494 11"
                    className="underline-stroke-main"
                    filter="url(#handDrawnFilter)"
                  />
                </svg>
              </span>
            </h1>

            <motion.div
              className="hero-narrative"
              variants={fadeUp} initial="hidden" animate="visible" custom={1.5}
            >
              <p>
                <strong>iMatrix Technologies Private Limited</strong><br />
                Founded in 2006, iMatrix Technologies began with a clear vision—to deliver innovative
                technology solutions backed by exceptional customer service. Over the years, what started
                as a modest initiative has evolved into a trusted partner for businesses across industries.
              </p>
              <p>
                As a recognized Tally 5 Star Partner, our core focus lies in delivering comprehensive Tally
                software solutions—sales, implementation, customization, and integration—combined with a
                customer-first approach that ensures seamless deployment and maximum value.
              </p>
              <p>
                Since our formal incorporation in 2013, we have expanded our reach and capabilities,
                helping businesses streamline operations, ensure statutory compliance, and achieve
                sustainable growth with reliable, scalable, future-ready solutions.
              </p>
            </motion.div>

            <motion.div
              className="hero-actions"
              variants={fadeUp} initial="hidden" animate="visible" custom={2}
            >
              <button className="btn-primary" onClick={() => navigate('/solutions')}>
                Explore Solutions <FaArrowRight />
              </button>
              <button className="btn-secondary" onClick={() => navigate('/contact')}>
                <FaPhoneAlt /> Book Consultation
              </button>
            </motion.div>

            <motion.div
              className="hero-stats-row"
              variants={fadeUp} initial="hidden" animate="visible" custom={3}
            >
              {STATS.map((s, idx) => (
                <div
                  key={s.label}
                  className="hero-stat-item"
                  onClick={idx === 0 ? handleStatTripleClick : undefined}
                  title={idx === 0 ? 'Psst… try triple-clicking 👀' : undefined}
                  style={idx === 0 ? { cursor: 'default' } : {}}
                >
                  <span className="hero-stat-item__value">{s.value}</span>
                  <span className="hero-stat-item__label">{s.label}</span>
                </div>
              ))}
            </motion.div>

            {/* Easter Egg: Founder Note Modal */}
            {showFounderNote && (
              <div
                className="founder-note-overlay"
                onClick={() => setShowFounderNote(false)}
                style={{
                  position: 'fixed', inset: 0, zIndex: 999990,
                  background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(6px)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px'
                }}
              >
                <div
                  className="founder-note-card"
                  onClick={e => e.stopPropagation()}
                  style={{
                    background: '#fffdf5',
                    border: '1px solid #e8e0cc',
                    borderRadius: '20px',
                    padding: '36px 40px',
                    maxWidth: '480px',
                    width: '100%',
                    boxShadow: '0 24px 64px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.8)',
                    fontFamily: 'Georgia, serif',
                    position: 'relative',
                    animation: 'ctx-menu-in 0.35s cubic-bezier(0.16,1,0.3,1) forwards'
                  }}
                >
                  {/* Wax seal */}
                  <div style={{
                    position: 'absolute', top: -20, right: 32,
                    width: 48, height: 48, borderRadius: '50%',
                    background: 'radial-gradient(circle at 35% 35%, #c0392b, #7b241c)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '1.2rem', boxShadow: '0 4px 12px rgba(192,57,43,0.4)'
                  }}>i</div>

                  <p style={{ color: '#888', fontSize: '0.75rem', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 16 }}>A note from our founder</p>
                  <p style={{ fontSize: '1.05rem', color: '#3d3320', lineHeight: 1.75, marginBottom: 16 }}>
                    “In 2006, we started iMatrix with nothing but a vision and an unshakeable
                    belief that technology could transform businesses of every size.
                    Twenty years later, 3000+ clients later — that belief has only grown stronger.”
                  </p>
                  <p style={{ fontSize: '0.88rem', color: '#7a6e58', fontStyle: 'italic' }}>— iMatrix Technologies Team</p>
                  <button
                    onClick={() => setShowFounderNote(false)}
                    style={{
                      marginTop: 20, background: 'none', border: '1px solid #c8b99a',
                      borderRadius: 8, padding: '8px 18px', cursor: 'pointer',
                      color: '#7a6e58', fontSize: '0.85rem', fontFamily: 'inherit'
                    }}
                  >Close</button>
                </div>
              </div>
            )}

          </div>

          {/* ── Right Visual ── */}
          <motion.div
            className="hero-visual-right"
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
          >
            {/* ✅ New unified carousel panel — replaces features-panel-glass + all ProductStack rows */}
            <SolutionsCarousel />
          </motion.div>

        </div>
      </section>

      {/* ── Services Loop Ticker ── */}
      {/* <motion.div
        className="hero-services-ticker"
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1, delay: 0.8 }}
      >
        <p className="ticker-label">Our Services</p> */}
        {/* <div className="service-loop-wrapper">
          <LogoLoop
            logos={SERVICES.map(s => ({ src: s.img, alt: s.label, title: s.label, route: s.route }))}
            speed={40}
            direction="left"
            fadeOut
            fadeOutColor="var(--bg)"
            renderItem={item => <ServiceCard item={item} navigate={navigate} />}
          />
        </div> */}
      {/* </motion.div> */}

      {/* ── Trust bar ── */}
      <div className="hero-trust">
        {TRUST.map(item => (
          <div key={item.label} className="trust-item">
            <span className="trust-item__icon">{item.icon}</span>
            <span>{item.label}</span>
          </div>
        ))}
      </div>

      {/* ── WhatsApp FAB ── */}
      <a
        className="whatsapp-fab"
        href="https://wa.me/919841609990"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Chat with us on WhatsApp"
      >
        <FaWhatsapp size={28} />
      </a>
    </div>
  );
};

export default HeroSection;